import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    IconButton,
    LinearProgress,
    Link,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import {useSession} from '../../../firebase/UserProvider';
import {getUsersByIds} from '../../../firebase/firestore/users';
import AuthorChip from '../../products/components/common/AuthorChip';
import openCollectionIir, {navigateToIirEditor} from '../viewer/openCollectionIir';
import {
    assignCollectionTask,
    createCollectionIir,
    createCollectionMaterial,
    claimCollectionTask,
    deleteCollectionMaterial,
    deleteCollectionTask,
    getCollectionTask,
    listCollectionMaterials,
    transitionCollectionTask,
    updateCollectionTask,
} from '../collectionTaskApi';
import {extractDroppedUploadEntries} from '../../products/reportEditorPage/leftPanel/components/sourcesDropUtils';
import useCollectionMaterialUploadQueueStore from '../components/useCollectionMaterialUploadQueueStore';
import {
    CM_ROLES,
    COLLECTOR_ROLES,
    IIR_CREATE_ROLES,
    collectionTaskPath,
    ctUiStatusFromTask,
    hasAnyRole,
    normalizeCtClassificationToLevel,
    sanitizeReturnTo,
    statusColor,
    statusLabel,
} from '../collectionTaskPayload';
import AddCollectionMaterialControl from '../components/AddCollectionMaterialControl';
import CollectionTaskAssigneeField from '../components/CollectionTaskAssigneeField';
import CollectionTaskStatusButton from '../components/CollectionTaskStatusButton';
import EntityTagsSection from '../../collection-requests/tags/EntityTagsSection';
import RequirementTargetsSection from '../../collection-requests/components/RequirementTargetsSection';
import {getClassificationByValue} from '../../../components/prosemirror-editor/helpers/classificationLevels';

const RESERVED_TASK_IDS = new Set(['new', 'create']);
const NOWRAP = {whiteSpace: 'nowrap'};
const TITLE_CELL = {
    maxWidth: 220,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
};

const CLASSIFICATION_CHIP_SX = {
    height: 20,
    alignSelf: 'flex-start',
    minWidth: 28,
    fontWeight: 700,
    '& .MuiChip-label': {px: 0.75},
};

/**
 * Read-only Level / Distribution / Compartment display for a Collection Task.
 * Mirrors the three fields on the create/edit form (`ClassificationFieldsGroup`) so values
 * saved there are visible on the detail page — the legacy free-string `classification` chip
 * alone drops Distribution/Compartment (e.g. ORCON / PHOENIX).
 */
function ClassificationMarkingsDisplay({level, distribution, compartment}) {
    const levelInfo = level ? getClassificationByValue(level) : null;
    const dist = Array.isArray(distribution) ? distribution.filter(Boolean) : [];
    const comp = Array.isArray(compartment) ? compartment.filter(Boolean) : [];

    const renderTags = (tags) => {
        if (tags.length === 0) {
            return (
                <Typography variant="body2" color="text.disabled" sx={{fontStyle: 'italic'}}>
                    Not set
                </Typography>
            );
        }
        return (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
                {tags.map((tag) => (
                    <Chip
                        key={tag}
                        size="small"
                        variant="outlined"
                        label={tag}
                        sx={{height: 20, minWidth: 28, '& .MuiChip-label': {px: 0.75}}}
                    />
                ))}
            </Stack>
        );
    };

    return (
        <Stack
            direction="row"
            spacing={1.5}
            mt={1}
            flexWrap="wrap"
            useFlexGap
            data-testid="ct-classification-markings"
        >
            <Stack spacing={0.25} sx={{minWidth: 80}}>
                <Typography variant="caption" color="text.secondary" sx={{lineHeight: 1.3}}>Level</Typography>
                {level ? (
                    <Chip
                        size="small"
                        label={levelInfo ? levelInfo.value : level}
                        sx={{
                            ...CLASSIFICATION_CHIP_SX,
                            color: levelInfo?.color,
                            bgcolor: levelInfo?.bgColor,
                        }}
                    />
                ) : (
                    <Typography variant="body2" color="text.disabled" sx={{fontStyle: 'italic'}}>—</Typography>
                )}
            </Stack>
            <Stack spacing={0.25} sx={{minWidth: 80}}>
                <Typography variant="caption" color="text.secondary" sx={{lineHeight: 1.3}}>Distribution</Typography>
                {renderTags(dist)}
            </Stack>
            <Stack spacing={0.25} sx={{minWidth: 80}}>
                <Typography variant="caption" color="text.secondary" sx={{lineHeight: 1.3}}>Compartment</Typography>
                {renderTags(comp)}
            </Stack>
        </Stack>
    );
}

function sourceKindIcon(sourceKind) {
    if (sourceKind === 'url') return <LinkOutlinedIcon fontSize="small"/>;
    if (sourceKind === 'raw') return <NotesOutlinedIcon fontSize="small"/>;
    return <InsertDriveFileOutlinedIcon fontSize="small"/>;
}

function sourceKindLabel(sourceKind) {
    if (sourceKind === 'url') return 'URL';
    if (sourceKind === 'raw') return 'Raw';
    if (sourceKind === 'file') return 'File';
    return sourceKind || '—';
}

function userDisplayName(u) {
    if (!u) return null;
    const fromParts = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
    return fromParts || u.displayName || u.name || u.email || null;
}

function toAvatarProfile(u) {
    if (!u) return null;
    return {
        displayName: userDisplayName(u),
        hasAvatar: u.hasAvatar === true,
        profilePicture: u.profilePicture || u.photoURL || undefined,
    };
}

function PersonMeta({label, userId, profile}) {
    if (!userId) {
        return (
            <Stack spacing={0.25} minWidth={0}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="body2" color="text.secondary">—</Typography>
            </Stack>
        );
    }
    return (
        <Stack spacing={0.25} minWidth={0}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <AuthorChip
                userId={userId}
                userDisplayName={userDisplayName(profile) || userId}
                flex={undefined}
                avatarProfile={toAvatarProfile(profile)}
            />
        </Stack>
    );
}

export default function CollectionTaskDetailPage() {
    const {taskId} = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const {user, role, roles} = useSession();
    const canManage = hasAnyRole(role, roles, CM_ROLES);
    const canCollect = hasAnyRole(role, roles, COLLECTOR_ROLES);
    const canCreateIir = hasAnyRole(role, roles, IIR_CREATE_ROLES);
    const isAdmin = hasAnyRole(role, roles, ['admin']);

    const [task, setTask] = useState(null);
    const [materials, setMaterials] = useState([]);
    const [people, setPeople] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteTaskOpen, setDeleteTaskOpen] = useState(false);
    const [isDragOverMaterials, setIsDragOverMaterials] = useState(false);
    const [dropRequest, setDropRequest] = useState(null);
    const dragDepthRef = useRef(0);
    const dropTokenRef = useRef(0);

    const uid = user?.uid || user?.id;
    const isAssignee = task?.assigneeId && uid && task.assigneeId === uid;
    // Stable API key: UUID when loaded (materials/tags require it); URL may be CT number.
    const taskApiId = task?.id || taskId;

    // Materials may only be added by the assignee (admins keep a global override); mirrored
    // below (post-loading-guard) as `canAddMaterial` for the render body. Computed here too
    // (as a ref) so the drag-and-drop handlers — registered before the early-return guards —
    // can gate drops without violating the rules of hooks.
    const canAddMaterial = Boolean(task) && canCollect
        && !['done', 'cancelled'].includes(ctUiStatusFromTask(task))
        && Boolean(task?.assigneeId) && (isAssignee || isAdmin);
    const canAddMaterialRef = useRef(canAddMaterial);
    useEffect(() => {
        canAddMaterialRef.current = canAddMaterial;
    }, [canAddMaterial]);

    useEffect(() => {
        if (!taskId || !RESERVED_TASK_IDS.has(String(taskId).toLowerCase())) {
            return;
        }
        navigate({
            pathname: '/collection/tasks/create',
            search: location.search,
        }, {replace: true, state: location.state});
    }, [taskId, location.search, location.state, navigate]);

    const reload = useCallback(async () => {
        if (!taskId || RESERVED_TASK_IDS.has(String(taskId).toLowerCase())) {
            return;
        }
        // Do NOT setLoading(true) here — that would unmount the page on every background
        // poll / queue-triggered refresh (once per 5 s while materials are processing).
        // `loading` starts true and is only ever set to false (below), so the initial
        // full-page spinner fires exactly once on first mount, and subsequent refreshes
        // silently update state in-place.
        setError(null);
        try {
            // Resolve by UUID or CT number; materials list filters on UUID.
            const t = await getCollectionTask(taskId);
            const mats = t?.id
                ? await listCollectionMaterials({taskId: t.id})
                : [];
            setTask(t);
            setMaterials(mats);

            const ids = [t?.assigneeId, t?.createdBy].filter(Boolean);
            if (ids.length) {
                const users = await getUsersByIds(ids);
                const byId = {};
                for (const u of users) {
                    const id = u.id || u.userId || u.uid;
                    if (id) byId[id] = u;
                }
                setPeople(byId);
            } else {
                setPeople({});
            }
        } catch (e) {
            setError(e?.message || 'Failed to load task');
        } finally {
            setLoading(false);
        }
    }, [taskId]);

    // When navigating to a different task, reset to loading state so the spinner
    // shows while new data loads (taskId change also recreates `reload` above and
    // re-triggers the effect below).
    useEffect(() => {
        setLoading(true);
        setTask(null);
        setMaterials([]);
        setPeople({});
        setError(null);
    }, [taskId]);

    useEffect(() => {
        reload();
    }, [reload]);

    // After create, targets can arrive asynchronously. Refetch once if the first load
    // is missing named targets.
    const shouldRefetchTargets = location.state?.refetchTargets === true;
    const didRefetchTargetsRef = useRef(false);
    useEffect(() => {
        if (!shouldRefetchTargets || didRefetchTargetsRef.current || !task) {
            return undefined;
        }
        const targets = Array.isArray(task.targets) ? task.targets : [];
        const hasNamedTargets = targets.some((t) => t && typeof t === 'object' && t.canonicalName);
        if (targets.length > 0 && hasNamedTargets) {
            didRefetchTargetsRef.current = true;
            return undefined;
        }
        didRefetchTargetsRef.current = true;
        let cancelled = false;
        getCollectionTask(taskId)
            .then((fresh) => {
                if (cancelled || !fresh) return;
                setTask((prev) => (prev ? {...prev, targets: fresh.targets ?? prev.targets} : fresh));
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [shouldRefetchTargets, task, taskId]);

    // Files dropped/uploaded via the background upload queue (CollectionMaterialFileImportDialog
    // enqueues and closes immediately — see AddCollectionMaterialControl) create their Collection
    // Material row synchronously server-side, but this page has no direct callback from the
    // queue's processor hook (mounted higher up, at CollectionLayout, so uploads survive
    // navigating to a different task). Instead, watch the shared queue store directly: once an
    // item for *this* task reports a materialId we haven't rendered yet, reload the list.
    const queueItems = useCollectionMaterialUploadQueueStore((state) => state.items);
    const retryQueueItem = useCollectionMaterialUploadQueueStore((state) => state.retryItem);
    const cancelQueueItem = useCollectionMaterialUploadQueueStore((state) => state.markCancelled);
    const removeQueueItem = useCollectionMaterialUploadQueueStore((state) => state.removeItem);
    // Active + duplicate queue items for THIS task — shown as rows in the materials table.
    // Ghost rows stay visible through the full pipeline (queued → uploading → processing).
    // They only disappear once status reaches 'success'/'cancelled' OR the real material row
    // already exists in the fetched materials list (e.g. page was refreshed mid-upload).
    const activeQueueItemsForTask = useMemo(() => {
        if (!taskApiId) return [];
        return queueItems.filter((item) =>
            String(item.collectionTaskId) === String(taskApiId)
            && ['queued', 'uploading', 'processing', 'paused', 'failed', 'duplicate'].includes(item.status)
            // Hide once the persisted row is already in the materials list so we never show both.
            && !materials.some((m) => m.id && item.materialId && String(m.id) === String(item.materialId)),
        );
    }, [queueItems, taskApiId, materials]);

    // Auto-dismiss duplicate rows after 8 s so the table self-cleans.
    const dismissedDuplicatesRef = useRef(new Set());
    useEffect(() => {
        const duplicates = activeQueueItemsForTask.filter((item) => item.status === 'duplicate');
        duplicates.forEach((item) => {
            if (dismissedDuplicatesRef.current.has(item.id)) return;
            dismissedDuplicatesRef.current.add(item.id);
            setTimeout(() => removeQueueItem(item.id), 8000);
        });
    }, [activeQueueItemsForTask, removeQueueItem]);
    const knownMaterialIdsRef = useRef(new Set());
    useEffect(() => {
        knownMaterialIdsRef.current = new Set(materials.map((m) => String(m.id)));
    }, [materials]);
    useEffect(() => {
        if (!taskApiId) {
            return;
        }
        // Only reload once the pipeline is fully done (status === 'success'), not merely when
        // the materialId first appears (that's just the upload HTTP completing — the extraction/
        // embedding pipeline is still running and the ghost row should stay visible until done).
        const hasNewSuccessful = queueItems.some((item) => (
            item.status === 'success'
            && item.materialId
            && String(item.collectionTaskId) === String(taskApiId)
            && !knownMaterialIdsRef.current.has(String(item.materialId))
        ));
        if (hasNewSuccessful) {
            reload();
        }
    }, [queueItems, taskApiId, reload]);

    // Poll every 5 s while any persisted material is still processing server-side.
    // Once all are done (uploadStatus != 'processing' or 'pending') the interval clears itself.
    useEffect(() => {
        const hasProcessing = materials.some(
            (m) => m.uploadStatus === 'processing' || m.uploadStatus === 'pending',
        );
        if (!hasProcessing) return;
        const id = setInterval(() => reload(), 5000);
        return () => clearInterval(id);
    }, [materials, reload]);

    const isFileDragEvent = useCallback((event) => {
        const types = event?.dataTransfer?.types;
        return Array.isArray(types) ? types.includes('Files') : types?.contains?.('Files');
    }, []);

    const resetMaterialsDragState = useCallback(() => {
        dragDepthRef.current = 0;
        setIsDragOverMaterials(false);
    }, []);

    const handleMaterialsDragOver = useCallback((event) => {
        if (!canAddMaterialRef.current || !isFileDragEvent(event)) {
            return;
        }
        event.preventDefault();
    }, [isFileDragEvent]);

    const handleMaterialsDragEnter = useCallback((event) => {
        if (!canAddMaterialRef.current || !isFileDragEvent(event)) {
            return;
        }
        event.preventDefault();
        dragDepthRef.current += 1;
        setIsDragOverMaterials(true);
    }, [isFileDragEvent]);

    const handleMaterialsDragLeave = useCallback((event) => {
        if (!canAddMaterialRef.current || !isFileDragEvent(event)) {
            return;
        }
        event.preventDefault();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) {
            setIsDragOverMaterials(false);
        }
    }, [isFileDragEvent]);

    const handleMaterialsDropCapture = useCallback((event) => {
        if (!canAddMaterialRef.current || !isFileDragEvent(event)) {
            return;
        }
        // Capture-phase: preventDefault before any child (e.g. TextField) can consume the drop.
        event.preventDefault();
        resetMaterialsDragState();

        const dataTransfer = event.dataTransfer;
        extractDroppedUploadEntries(dataTransfer)
            .then((entries) => {
                if (!entries.length) return;
                dropTokenRef.current += 1;
                setDropRequest({files: entries.map((entry) => entry.file), token: dropTokenRef.current});
            })
            .catch((dropError) => {
                setError(dropError?.message || 'Failed to read dropped files.');
            });
    }, [isFileDragEvent, resetMaterialsDragState]);

    // Canonicalize UUID (or other alias) URLs to the displayed CT number.
    useEffect(() => {
        if (!task?.ctNumber || !taskId) return;
        const current = decodeURIComponent(String(taskId));
        if (current === task.ctNumber) return;
        navigate(
            {
                pathname: collectionTaskPath(task),
                search: location.search,
                hash: location.hash,
            },
            {replace: true, state: location.state},
        );
    }, [task, taskId, location.search, location.hash, location.state, navigate]);

    const createdLabel = useMemo(() => (
        task?.createdAt ? new Date(task.createdAt).toLocaleString() : null
    ), [task?.createdAt]);

    const handleBack = () => {
        const returnTo = sanitizeReturnTo(location.state?.returnTo);
        if (returnTo) {
            navigate(returnTo);
            return;
        }
        navigate(-1);
    };

    const doTransition = async (status, statusComment) => {
        setBusy(true);
        setError(null);
        try {
            await transitionCollectionTask(taskApiId, status, statusComment);
            await reload();
        } catch (e) {
            setError(e?.message || 'Status update failed');
        } finally {
            setBusy(false);
        }
    };

    const handleClaim = async () => {
        setBusy(true);
        setError(null);
        try {
            await claimCollectionTask(taskApiId);
            await reload();
        } catch (e) {
            setError(e?.message || 'Failed to claim task');
        } finally {
            setBusy(false);
        }
    };

    const handleAssign = async (assigneeId) => {
        setBusy(true);
        setError(null);
        try {
            if (assigneeId) {
                await assignCollectionTask(taskApiId, assigneeId);
            } else {
                await updateCollectionTask(taskApiId, {assigneeId: null});
            }
            await reload();
        } catch (e) {
            setError(e?.message || 'Failed to assign task');
        } finally {
            setBusy(false);
        }
    };

    const handleImportMaterial = async (entry) => {
        setBusy(true);
        setError(null);
        try {
            // Note: the 'file' sourceKind is handled entirely by CollectionMaterialFileImportDialog
            // via the background upload queue (useCollectionMaterialUploadQueueStore) — it enqueues
            // and closes without calling onImport, so files never reach this function. This branch
            // only ever sees 'url' and 'raw'.
            if (entry.sourceKind === 'url') {
                const urls = entry.urls || (entry.url ? [entry.url] : []);
                let last = null;
                for (const url of urls) {
                    last = await createCollectionMaterial({
                        collectionTaskId: taskApiId,
                        sourceKind: 'url',
                        url,
                        title: entry.title,
                        collectionMethod: entry.collectionMethod,
                        classificationLevel: entry.classificationLevel,
                        distribution: entry.distribution,
                        compartment: entry.compartment,
                    });
                }
                await reload();
                return;
            }
            if (entry.sourceKind === 'raw') {
                const mat = await createCollectionMaterial({
                    collectionTaskId: taskApiId,
                    sourceKind: 'raw',
                    title: entry.title,
                    plainText: entry.plainText,
                    htmlContent: entry.htmlContent,
                    collectionMethod: entry.collectionMethod,
                    classificationLevel: entry.classificationLevel,
                    distribution: entry.distribution,
                    compartment: entry.compartment,
                });
                await reload();
            }
        } catch (e) {
            setError(e?.message || 'Failed to create material');
            throw e;
        } finally {
            setBusy(false);
        }
    };

    const cmPageReturnTo = `${location.pathname}${location.search || ''}${location.hash || ''}`;

    const navigateToMaterialPage = (material) => {
        navigate(`/collection/data/${material.id}/edit`, {
            state: {returnTo: cmPageReturnTo},
        });
    };

    const handleDeleteMaterial = async () => {
        if (!deleteTarget?.id) return;
        setBusy(true);
        setError(null);
        try {
            await deleteCollectionMaterial(deleteTarget.id);
            // Evict any upload-queue ghost that references the deleted material so it
            // doesn't re-appear as a "processing" row after the materials list reloads.
            const deletedId = deleteTarget.id;
            queueItems
                .filter((item) => item.materialId && String(item.materialId) === String(deletedId))
                .forEach((item) => removeQueueItem(item.id));
            setDeleteTarget(null);
            await reload();
        } catch (e) {
            setError(e?.message || 'Failed to delete material');
        } finally {
            setBusy(false);
        }
    };

    const handleDeleteTask = async () => {
        setBusy(true);
        setError(null);
        try {
            await deleteCollectionTask(taskApiId || taskId);
            navigate('/collection/tasks', {replace: true});
        } catch (e) {
            setError(e?.message || 'Failed to delete task');
            setDeleteTaskOpen(false);
        } finally {
            setBusy(false);
        }
    };

    const handleCreateIir = async () => {
        setBusy(true);
        try {
            // Title is LLM-suggested from collected materials on the API
            // (brief one-phrase summary, no "IIR:" prefix).
            const iir = await createCollectionIir({collectionTaskId: taskApiId});
            navigateToIirEditor({
                reportId: iir.reportId || iir.id,
                navigate,
                location,
            });
        } catch (e) {
            setError(e?.message || 'Failed to create IIR');
            setBusy(false);
        }
    };

    if (loading) {
        return <Stack alignItems="center" py={6}><CircularProgress/></Stack>;
    }
    if (!task) {
        return <Box p={2}><Alert severity="error">{error || 'Task not found'}</Alert></Box>;
    }

    const uiStatus = ctUiStatusFromTask(task);
    const taskDone = uiStatus === 'done' || uiStatus === 'cancelled';
    const showRowActions = uiStatus !== 'done';
    // Materials may only be added by the assignee (admins keep a global override); computed
    // above (`canAddMaterial`) so the drag-and-drop handlers registered before the early-return
    // guards above can use it too — reused here as-is.
    const showAssignFirstHint = canCollect && !taskDone && !task.assigneeId;

    return (
        <Box p={2} maxWidth={960} mx="auto">
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
                mb={1.5}
            >
                <Button
                    variant="outlined"
                    size="small"
                    sx={{...NOWRAP, minWidth: 'auto'}}
                    onClick={handleBack}
                >
                    Back
                </Button>
                <Stack
                    direction="row"
                    spacing={1}
                    flexShrink={0}
                    sx={NOWRAP}
                    data-testid="ct-header-actions"
                >
                    {(canManage || isAssignee) && (
                        <CollectionTaskStatusButton
                            task={task}
                            canManage={canManage}
                            isAssignee={isAssignee}
                            disabled={busy}
                            onTransition={(storedStatus) => doTransition(storedStatus)}
                        />
                    )}
                    {canManage && !taskDone && (
                        <Button
                            variant="outlined"
                            size="small"
                            sx={NOWRAP}
                            onClick={() => navigate(`${collectionTaskPath(task)}/edit`)}
                        >
                            Edit
                        </Button>
                    )}
                    {canManage && (
                        <Tooltip title="Delete task">
                            <IconButton
                                size="small"
                                color="error"
                                disabled={busy}
                                aria-label="Delete task"
                                onClick={() => setDeleteTaskOpen(true)}
                            >
                                <DeleteOutlineIcon fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
            </Stack>

            {/* ── Task header ── */}
            <Box mb={1.5}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap mb={0.25}>
                    <Typography variant="overline" color="text.secondary" sx={{lineHeight: 1.6, letterSpacing: 1}}>
                        {task.ctNumber}
                    </Typography>
                    {/* Status is already shown as the action button top-right; only show priority here */}
                    <Chip
                        size="small"
                        label={task.priority}
                        variant="outlined"
                        sx={{
                            height: 18,
                            fontWeight: 600,
                            fontSize: '0.68rem',
                            letterSpacing: 0.4,
                            '& .MuiChip-label': {px: 0.75},
                        }}
                    />
                </Stack>
                <Typography variant="h6" sx={{fontWeight: 600, lineHeight: 1.4}}>{task.title}</Typography>
            </Box>

            {/* ── Compact metadata grid ── */}
            <Stack
                direction="row"
                spacing={2}
                flexWrap="wrap"
                useFlexGap
                mb={1.5}
                sx={{
                    '& .meta-item': {
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px',
                        minWidth: 80,
                    },
                }}
            >
                {/* Classification */}
                <ClassificationMarkingsDisplay
                    level={task.classificationLevel
                        || normalizeCtClassificationToLevel(task.classification)}
                    distribution={task.distribution}
                    compartment={task.compartment}
                />
            </Stack>

            {/* ── People row ── */}
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap mb={1.5} alignItems="flex-start">
                <CollectionTaskAssigneeField
                    assigneeId={task.assigneeId}
                    profile={people[task.assigneeId]}
                    canManage={canManage && !taskDone}
                    canClaim={!taskDone && task.canClaim && canCollect}
                    disabled={busy}
                    onAssign={handleAssign}
                    onClaim={handleClaim}
                />
                <PersonMeta
                    label="Created by"
                    userId={task.createdBy}
                    profile={people[task.createdBy]}
                />
                {createdLabel && (
                    <Stack spacing={0} sx={{minWidth: 80}}>
                        <Typography variant="caption" color="text.secondary" sx={{lineHeight: 1.3}}>Created</Typography>
                        <Typography variant="body2" color="text.primary">{createdLabel}</Typography>
                    </Stack>
                )}
            </Stack>

            {error && <Alert severity="error" sx={{mb: 1.5}}>{error}</Alert>}

            {task.description && (
                <Typography variant="body2" color="text.secondary" whiteSpace="pre-wrap" mb={1.5}>
                    {task.description}
                </Typography>
            )}

            <EntityTagsSection
                entityType="CT"
                entityId={task.id || taskApiId}
                canEdit={canManage || isAssignee}
                enabled={Boolean(task.id || taskApiId)}
            />

            <Box mt={1.5}>
                <RequirementTargetsSection
                    value={task.targets}
                    canEdit={canManage && Boolean(task.id || taskApiId)}
                    onSave={async (refs) => {
                        const updated = await updateCollectionTask(taskApiId, {targets: refs});
                        setTask(updated || {
                            ...task,
                            targets: refs,
                            targetsLocked: true,
                        });
                    }}
                />
            </Box>

            <Box mt={2.5}>
                <Typography variant="subtitle2" gutterBottom sx={{color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1}}>Linked EEIs</Typography>
                <Stack spacing={0.25} mb={1.5}>
                    {(task.eeis || []).length === 0 && <Typography variant="body2" color="text.disabled">None</Typography>}
                    {(task.eeis || []).map((eei) => {
                        const num = eei.eeiNumber || eei.id;
                        return (
                            <Typography key={eei.id} variant="body2" sx={{lineHeight: 1.6}}>
                                <Link
                                    component="button"
                                    type="button"
                                    underline="hover"
                                    onClick={() => navigate(`/icr/eei/${encodeURIComponent(num)}`)}
                                    sx={{fontFamily: 'monospace', fontWeight: 600, mr: 1, verticalAlign: 'baseline'}}
                                >
                                    {num}
                                </Link>
                                {eei.text || ''}
                            </Typography>
                        );
                    })}
                </Stack>
            </Box>

            {task.statusComment && (
                <>
                    <Divider sx={{my: 1.5}}/>
                    <Typography variant="body2" color="text.secondary">Status comment: {task.statusComment}</Typography>
                </>
            )}

            {uiStatus === 'created' && (
                <Alert severity="info" sx={{mt: 1.5}}>
                    {canCollect && !canManage
                        ? 'This task is unassigned. Claim it with Assign to me to start collecting.'
                        : 'This task is unassigned. Assign a collector to start collection.'}
                </Alert>
            )}

            <Divider sx={{my: 1.5}}/>

            <Box
                width="100%"
                data-testid="ct-materials-dropzone"
                sx={{
                    transition: 'background-color 120ms ease',
                    bgcolor: isDragOverMaterials ? '#dbe8ff' : 'transparent',
                    borderRadius: 1,
                }}
                onDragOverCapture={handleMaterialsDragOver}
                onDragEnterCapture={handleMaterialsDragEnter}
                onDragLeaveCapture={handleMaterialsDragLeave}
                onDropCapture={handleMaterialsDropCapture}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.75}>
                    <Typography variant="subtitle2" sx={{color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1}}>Collected Materials</Typography>
                    {canAddMaterial && (
                        <AddCollectionMaterialControl
                            onImport={handleImportMaterial}
                            busy={busy}
                            initialClassificationLevel={task?.classificationLevel
                                || normalizeCtClassificationToLevel(task?.classification)}
                            initialDistribution={Array.isArray(task?.distribution) ? task.distribution : []}
                            initialCompartment={Array.isArray(task?.compartment) ? task.compartment : []}
                            collectionTaskId={taskApiId}
                            dropRequest={dropRequest}
                        />
                    )}
                    {showAssignFirstHint && (
                        <Tooltip title="Assign the task first">
                            <span data-testid="add-material-disabled-wrapper">
                                <Button variant="outlined" disabled>
                                    Add Material
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                </Stack>
                {materials.length === 0 ? (
                    <Typography color="text.secondary" mb={2}>No materials attached yet.</Typography>
                ) : (
                    <Paper variant="outlined" sx={{mb: 2, overflow: 'auto'}}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={NOWRAP}>CM #</TableCell>
                                    <TableCell>Title</TableCell>
                                    <TableCell sx={NOWRAP}>Source</TableCell>
                                    <TableCell sx={NOWRAP}>Method</TableCell>
                                    <TableCell sx={NOWRAP}>Created</TableCell>
                                    <TableCell sx={NOWRAP}>Status</TableCell>
                                    {showRowActions && <TableCell align="right" sx={NOWRAP}>Actions</TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {materials.map((m) => (
                                    <TableRow
                                        key={m.id}
                                        hover
                                        sx={{cursor: 'pointer'}}
                                        onClick={() => navigateToMaterialPage(m)}
                                    >
                                        <TableCell sx={{fontFamily: 'monospace', ...NOWRAP}}>
                                            {m.cmNumber || m.id}
                                        </TableCell>
                                        <TableCell sx={TITLE_CELL}>
                                            <Tooltip title={m.title || ''} placement="top-start" enterDelay={400}>
                                                <span>{m.title || '—'}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={NOWRAP}>
                                            <Stack direction="row" spacing={0.5} alignItems="center" sx={NOWRAP}>
                                                {sourceKindIcon(m.sourceKind)}
                                                <Typography variant="body2" sx={NOWRAP}>
                                                    {sourceKindLabel(m.sourceKind)}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={NOWRAP}>{m.collectionMethod || '—'}</TableCell>
                                        <TableCell sx={NOWRAP}>
                                            <Typography variant="body2" sx={NOWRAP} noWrap>
                                                {m.createdAt ? new Date(m.createdAt).toLocaleDateString() : '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={NOWRAP}>
                                            <Stack spacing={0.4} sx={{minWidth: 90}}>
                                                <Chip
                                                    size="small"
                                                    label={m.status === 'submitted' ? 'Submitted' : 'Draft'}
                                                    color={m.status === 'submitted' ? 'success' : 'default'}
                                                    variant={m.status === 'submitted' ? 'filled' : 'outlined'}
                                                />
                                                {m.uploadStatus === 'processing' && (
                                                    <>
                                                        <Chip
                                                            size="small"
                                                            label="Reprocessing…"
                                                            color="warning"
                                                            variant="outlined"
                                                        />
                                                        {m.uploadProgressPercent != null && (
                                                            <Stack direction="row" spacing={0.75} alignItems="center">
                                                                <LinearProgress
                                                                    variant="determinate"
                                                                    value={m.uploadProgressPercent}
                                                                    sx={{height: 3, borderRadius: 1, flex: 1}}
                                                                />
                                                                <Typography variant="caption" color="text.secondary" sx={{minWidth: 24, textAlign: 'right'}}>
                                                                    {m.uploadProgressPercent}%
                                                                </Typography>
                                                            </Stack>
                                                        )}
                                                    </>
                                                )}
                                            </Stack>
                                        </TableCell>
                                        {showRowActions && (
                                            <TableCell align="right" sx={NOWRAP} onClick={(e) => e.stopPropagation()}>
                                                <IconButton
                                                    size="small"
                                                    aria-label="Edit material"
                                                    disabled={busy}
                                                    onClick={() => navigateToMaterialPage(m)}
                                                >
                                                    <EditOutlinedIcon fontSize="small"/>
                                                </IconButton>
                                                {(canManage || canCollect) && m.canDelete && (
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        aria-label="Delete material"
                                                        disabled={busy}
                                                        onClick={() => setDeleteTarget(m)}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small"/>
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}

                                {/* ── In-progress upload queue rows ── */}
                                {activeQueueItemsForTask.map((item) => {
                                    const isActive = item.status === 'uploading' || item.status === 'processing';
                                    const isFailed = item.status === 'failed';
                                    const isDuplicate = item.status === 'duplicate';
                                    // Upload transfer progress (0-100), only meaningful for 'uploading' phase.
                                    const uploadProgress = item.status === 'uploading'
                                        ? (item.progressPercent ?? item.progress ?? 0)
                                        : 0;
                                    return (
                                        <TableRow
                                            key={item.id}
                                            sx={{
                                                opacity: (isFailed || isDuplicate) ? 0.85 : 1,
                                                '& td:first-of-type': {
                                                    borderLeft: '3px solid',
                                                    borderLeftColor: isFailed ? 'error.main' : isDuplicate ? 'warning.main' : 'primary.main',
                                                },
                                            }}
                                        >
                                            <TableCell sx={{fontFamily: 'monospace', ...NOWRAP, color: 'text.disabled'}}>
                                                —
                                            </TableCell>
                                            <TableCell sx={TITLE_CELL}>
                                                <Stack spacing={0.25}>
                                                    <Typography variant="body2" noWrap color={isFailed ? 'error' : isDuplicate ? 'warning.dark' : 'text.primary'}>
                                                        {item.title || item.name}
                                                    </Typography>
                                                    {item.status === 'uploading' && (
                                                        <Stack direction="row" spacing={0.75} alignItems="center">
                                                            <LinearProgress
                                                                variant={uploadProgress > 0 ? 'determinate' : 'indeterminate'}
                                                                value={uploadProgress}
                                                                sx={{height: 3, borderRadius: 1, flex: 1, minWidth: 60}}
                                                            />
                                                            {uploadProgress > 0 && (
                                                                <Typography variant="caption" color="text.secondary" sx={{minWidth: 24}}>
                                                                    {uploadProgress}%
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    )}
                                                    {item.status === 'processing' && (
                                                        <Stack direction="row" spacing={0.75} alignItems="center">
                                                            <LinearProgress
                                                                variant={item.progressPercent != null && item.progressPercent > 0 ? 'determinate' : 'indeterminate'}
                                                                value={item.progressPercent ?? 0}
                                                                sx={{height: 3, borderRadius: 1, flex: 1, minWidth: 60}}
                                                            />
                                                            {item.progressPercent != null && item.progressPercent > 0 && (
                                                                <Typography variant="caption" color="text.secondary" sx={{minWidth: 24}}>
                                                                    {item.progressPercent}%
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    )}
                                                    {(isFailed || isDuplicate) && item.error && (
                                                        <Typography variant="caption" color={isFailed ? 'error' : 'warning.dark'} noWrap>
                                                            {item.error}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                            <TableCell sx={NOWRAP}>
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    <InsertDriveFileOutlinedIcon fontSize="small" sx={{color: 'text.disabled'}}/>
                                                    <Typography variant="body2" color="text.secondary" sx={NOWRAP}>File</Typography>
                                                </Stack>
                                            </TableCell>
                                            <TableCell sx={NOWRAP}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {item.collectionMethod || '—'}
                                                </Typography>
                                            </TableCell>
                                            {/* Created — blank for in-flight queue rows */}
                                            <TableCell sx={NOWRAP}>—</TableCell>
                                            <TableCell sx={NOWRAP}>
                                                <Chip
                                                    size="small"
                                                    label={
                                                        isDuplicate ? 'Already exists' :
                                                        item.status === 'queued' ? 'Queued' :
                                                        item.status === 'uploading' ? 'Uploading…' :
                                                        item.status === 'processing' ? 'Processing…' :
                                                        item.status === 'paused' ? 'Paused' :
                                                        'Failed'
                                                    }
                                                    color={isFailed ? 'error' : (isDuplicate || item.status === 'processing') ? 'warning' : 'default'}
                                                    variant="outlined"
                                                    icon={isActive ? <CircularProgress size={10} sx={{ml: '6px !important'}}/> : undefined}
                                                />
                                            </TableCell>
                                            {showRowActions && (
                                                <TableCell align="right" sx={NOWRAP}>
                                                    {isFailed && (
                                                        <Tooltip title="Retry upload">
                                                            <IconButton size="small" onClick={() => retryQueueItem(item.id)}>
                                                                <ReplayRoundedIcon fontSize="small"/>
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {isDuplicate && (
                                                        <Tooltip title="Dismiss">
                                                            <IconButton size="small" onClick={() => removeQueueItem(item.id)}>
                                                                <DeleteOutlineIcon fontSize="small"/>
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {!isFailed && !isDuplicate && (
                                                        <Tooltip title="Cancel upload">
                                                            <IconButton size="small" onClick={() => cancelQueueItem(item.id, 'Cancelled')}>
                                                                <DeleteOutlineIcon fontSize="small"/>
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Paper>
                )}

                {canCreateIir && uiStatus === 'done' && !task.iir && (
                    <Tooltip title="Intelligence Information Report" arrow placement="top">
                        <span>
                            <Button variant="contained" disabled={busy} onClick={handleCreateIir} sx={{mb: 2}}>
                                Create IIR
                            </Button>
                        </span>
                    </Tooltip>
                )}
            </Box>

            {task.iir && (
                <Box width="100%" mt={1.5} data-testid="ct-iir-section">
                    <Tooltip title="Intelligence Information Report" arrow placement="top">
                        <Typography variant="subtitle2" mb={0.75} sx={{display: 'inline-block', color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1}}>IIR</Typography>
                    </Tooltip>
                    <Paper variant="outlined" sx={{p: 1.5}}>
                        <Stack spacing={1} alignItems="flex-start">
                            <Link
                                component="button"
                                type="button"
                                underline="hover"
                                onClick={() => openCollectionIir({
                                    iir: task.iir,
                                    userId: uid,
                                    navigate,
                                    location,
                                })}
                                sx={{textAlign: 'left'}}
                            >
                                {task.iir.title || task.iir.id}
                            </Link>
                            <Chip
                                size="small"
                                label={statusLabel(task.iir.workflowStatus || task.iir.status || 'draft')}
                                variant="outlined"
                            />
                        </Stack>
                    </Paper>
                </Box>
            )}

            <Dialog open={Boolean(deleteTarget)} onClose={() => !busy && setDeleteTarget(null)}>
                <DialogTitle>Delete collected material?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This removes {deleteTarget?.cmNumber || 'this material'} and its linked uploaded
                        content from search. This cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button disabled={busy} onClick={() => setDeleteTarget(null)}>Cancel</Button>
                    <Button color="error" variant="contained" disabled={busy} onClick={handleDeleteMaterial}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteTaskOpen} onClose={() => !busy && setDeleteTaskOpen(false)}>
                <DialogTitle>Delete collection task?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This permanently removes <strong>{task.ctNumber}</strong> — all associated
                        materials and links will be deleted. This cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button disabled={busy} onClick={() => setDeleteTaskOpen(false)}>Cancel</Button>
                    <Button color="error" variant="contained" disabled={busy} onClick={handleDeleteTask}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
